// 工地剖面:每一层画成它此刻所处的工序。画法见 11-美术.md 的「施工中的楼」。
// 只用接口给的公开字段(工序、进度、做法、验收、裂缝、加固、砌墙和外立面进度),看不到的东西不画。
import type { FloorView, Project } from './api.ts';
import { colors, drawGrain, drawRain, drawSky, facade, fitCanvas, hash, skyAt } from './art.ts';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class Section {
  private c: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private p: Project | null = null;
  private weather = 'sunny';
  private raf = 0;
  private floorRects: { n: number; r: Rect }[] = [];
  private lit: Rect[] = [];
  private selected = 0;
  onFloor: ((n: number) => void) | null = null;
  /** 还没开工的楼层(虚线轮廓)也能点(单机版的工地页:点了给那一层定做法)。 */
  pickFuture = false;
  /** 每层旁边写的账:省多少、按做法强度是图纸的几成(bar 是 0 到 1.2),颜色按好坏。没有的不写。 */
  notes: ((n: number) => { text: string; bar: number; tone: 'bad' | 'warn' | 'ok' } | null) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.c = canvas;
    this.g = canvas.getContext('2d')!;
    canvas.addEventListener('click', (e) => {
      // 单机版的工地页:一整行都能点(右边写着这一层的账)
      const hit = this.floorRects.find((f) => e.offsetY >= f.r.y && e.offsetY < f.r.y + f.r.h && (this.pickFuture || (e.offsetX >= f.r.x - 20 && e.offsetX < f.r.x + f.r.w + 20)));
      if (hit) {
        this.selected = hit.n;
        this.onFloor?.(hit.n);
      }
    });
    this.raf = requestAnimationFrame(this.frame);
  }

  destroy() {
    cancelAnimationFrame(this.raf);
  }

  // 服务端每个游戏小时(现实 2.5 分钟)推进一次。新的状态到了,画面在这一小时里从上一个样子慢慢长到新的样子,
  // 盯着看就能看见模板一块块支起来、钢筋一根根铺开、混凝土一点点铺满。
  private prev: Project | null = null;
  private at = 0;
  static tweenMs = 150_000;

  set(p: Project, weather: string) {
    if (this.p && this.p.id === p.id && this.p.hour !== p.hour) {
      this.prev = this.shown(performance.now());
      this.at = performance.now();
    } else if (!this.p || this.p.id !== p.id) {
      this.prev = null;
    }
    this.p = p;
    this.weather = weather;
  }

  /** 此刻画出来的样子:上一小时和这一小时之间插值。 */
  private shown(now: number): Project {
    const p = this.p!;
    const a = this.prev;
    if (!a || a.id !== p.id) return p;
    const u = Math.min(1, (now - this.at) / Section.tweenMs);
    if (u >= 1) return p;
    const lerp = (x: number, y: number) => x + (y - x) * u;
    return {
      ...p,
      stageDone: a.stage === p.stage ? lerp(a.stageDone, p.stageDone) : p.stageDone,
      floors: p.floors.map((f, i) => {
        const o = a.floors[i];
        if (!o) return u < 0.15 ? { ...f, phase: 'wait' } : f; // 新开的一层稍后才出现
        return {
          ...f,
          phaseDone: o.phase === f.phase ? lerp(o.phaseDone, f.phaseDone) : f.phaseDone,
          masonry: lerp(o.masonry, f.masonry),
          facade: lerp(o.facade, f.facade),
        };
      }),
    };
  }

  select(n: number) {
    this.selected = n;
  }

  private frame = (t: number) => {
    this.raf = requestAnimationFrame(this.frame);
    const [W, H] = fitCanvas(this.c);
    if (!W || !H || !this.p) return;
    this.draw(W, H, t, this.shown(t));
  };

  private draw(W: number, H: number, t: number, p: Project) {
    const g = this.g;
    const sky = skyAt(p.hour);
    drawSky(g, W, H, sky);
    this.lit = [];
    this.floorRects = [];

    const groundY = Math.round(H * 0.8);
    // 远景:一排低对比的楼影,盖着地平线的雾
    for (let i = 0; i < 14; i++) {
      const bw = 18 + hash(i * 3) * 34;
      const bh = 20 + hash(i * 7) * 70;
      const bx = (i / 14) * W + hash(i * 11) * 20 - 10;
      g.fillStyle = sky.night > 0.5 ? '#1c2742' : 'rgba(120,130,140,0.35)';
      g.fillRect(bx, groundY - bh, bw, bh);
      if (sky.night > 0.5) {
        for (let k = 0; k < 4; k++) {
          if (hash(i * 17 + k) < 0.5) {
            g.fillStyle = 'rgba(245,213,143,0.5)';
            g.fillRect(bx + 3 + hash(i * 19 + k) * (bw - 6), groundY - bh + 4 + hash(i * 23 + k) * (bh - 8), 2, 2);
          }
        }
      }
    }
    g.fillStyle = sky.haze;
    g.fillRect(0, groundY - 70, W, 70);

    // 地面和地下
    g.fillStyle = colors.ground;
    g.fillRect(0, groundY, W, H - groundY);
    g.fillStyle = 'rgba(0,0,0,0.12)';
    for (let i = 0; i < 40; i++) g.fillRect(hash(i * 5) * W, groundY + 4 + hash(i * 9) * (H - groundY - 6), 3, 1.5);
    g.fillStyle = '#6d5840';
    g.fillRect(0, groundY, W, 3);

    const target = Math.max(p.targetFloors, p.floors.length, 1);
    // 右边要写每层的账的,楼往左让出一块
    const room = this.notes ? Math.min(160, W * 0.34) : 0;
    const bw = Math.min((W - room) * 0.46, 240);
    const bx = Math.round((W - room) * 0.47 - bw / 2);
    const capH = 8;
    const fh = Math.max(5, Math.min(28, (groundY - capH - 46) / (target + 0.6)));
    const baseY = groundY - capH;

    if (p.stage === 'collapsed') {
      this.rubble(bx, bw, groundY, p.floors.length, fh);
      this.finish(W, H, t, sky.night);
      return;
    }

    this.piles(p, bx, bw, groundY, H);
    if (p.stage !== 'prep' && p.stage !== 'piles') {
      const doneCaps = p.stage === 'caps' ? p.stageDone : 1;
      g.fillStyle = colors.concrete;
      g.fillRect(bx - 6, groundY - capH, (bw + 12) * doneCaps, capH + 6);
      g.fillStyle = 'rgba(0,0,0,0.15)';
      g.fillRect(bx - 6, groundY - 1, (bw + 12) * doneCaps, 1);
    }

    const delivered = p.stage === 'delivered' || p.stage === 'acceptance';
    let top = 0; // 最高的一层有动静的楼层
    // 地基不够的楼整栋微微歪(按真实的度数画,不夸张)
    const lean = p.lean ?? 0;
    g.save();
    if (lean > 0) {
      g.translate(bx + bw / 2, baseY);
      g.rotate((lean * Math.PI) / 180);
      g.translate(-(bx + bw / 2), -baseY);
    }
    const marks = p.marks ?? [];
    p.floors.forEach((f, i) => {
      const w = bw * Math.min(1.45, p.widths?.[i] ?? 1); // 自己画的楼:每层宽窄如实画(太宽的收一点,免得出画面)
      // 放线偏了的层,和上下层错开一点
      const shift = marks.some((m) => m.floor === f.n && m.kind === 'offset') ? (f.n % 2 ? 2 : -2) : 0;
      const r = { x: bx + (bw - w) / 2 + shift, y: baseY - (i + 1) * fh, w, h: fh };
      if (f.phase !== 'wait') top = i + 1;
      this.floorRects.push({ n: f.n, r });
      this.floor(p, f, r, t, delivered, sky.night);
      this.marksOf(marks.filter((m) => m.floor === f.n).map((m) => m.kind), r, f.n);
    });
    if (marks.some((m) => m.kind === 'leak') && p.floors.length) {
      // 顶上渗水:顶层外墙一片水渍往下淌
      const r = this.floorRects[this.floorRects.length - 1].r;
      g.fillStyle = 'rgba(70,64,52,0.28)';
      g.beginPath();
      g.ellipse(r.x + r.w * 0.62, r.y + r.h * 0.35, r.w * 0.12, r.h * 0.45, 0, 0, Math.PI * 2);
      g.fill();
      g.fillRect(r.x + r.w * 0.6, r.y + r.h * 0.5, 2, r.h * 0.9);
    }
    // 还没开工的楼层:按设计画一个很淡的虚线轮廓,知道要盖多高
    for (let i = top; i < target; i++) {
      g.strokeStyle = 'rgba(40,40,40,0.14)';
      g.setLineDash([3, 4]);
      g.strokeRect(bx + 0.5, baseY - (i + 1) * fh + 0.5, bw - 1, fh - 1);
      g.setLineDash([]);
      if (this.pickFuture && i >= p.floors.length) this.floorRects.push({ n: i + 1, r: { x: bx, y: baseY - (i + 1) * fh, w: bw, h: fh } });
    }
    if (delivered) {
      this.roof(p, bx, bw, baseY - p.floors.length * fh, t);
      // 入口雨棚和门
      g.fillStyle = 'rgba(40,40,40,0.55)';
      g.fillRect(bx + bw * 0.4, baseY - Math.min(fh * 0.8, 16), bw * 0.2, Math.min(fh * 0.8, 16));
      g.fillStyle = colors.concreteDark;
      g.fillRect(bx + bw * 0.36, baseY - Math.min(fh * 0.8, 16) - 2, bw * 0.28, 2);
    }
    g.restore();

    const building = ['caps', 'structure', 'finishing'].includes(p.stage);
    if (building || p.stage === 'prep' || p.stage === 'piles') {
      this.fence(bx, bw, groundY, W);
    }
    if (building && top > 0) {
      this.scaffold(bx + bw, baseY, top, fh);
    }
    if (building) {
      const craneTop = baseY - (top + 3) * fh - 20;
      // 塔吊把料吊到正在干活的那一层:支模吊模板、绑钢筋吊钢筋、砌墙吊砌块。
      const active = p.floors.findIndex((f) => ['formwork', 'rebar', 'layout'].includes(f.phase));
      const load = active >= 0 ? (p.floors[active].phase === 'rebar' ? 'rebar' : 'wood') : p.stage === 'finishing' ? 'block' : 'rebar';
      const floorY = active >= 0 ? baseY - active * fh : baseY - top * fh;
      this.crane(bx - 26, groundY, Math.min(craneTop, groundY - 90), bw, t, { x: bx + bw * (0.3 + 0.4 * ((top * 37) % 10) / 10), y: floorY - 2, load });
    }
    const pouring = p.floors.find((f) => f.phase === 'pour');
    if (pouring) {
      const i = p.floors.indexOf(pouring);
      this.pump(bx + bw, groundY, baseY - (i + 1) * fh, W);
    }
    if (p.stage === 'piles') this.pileDriver(bx + bw * p.stageDone, groundY, t);
    if (p.stage === 'prep') this.hut(bx + bw * 0.6, groundY);

    if (building) this.workers(p, baseY, fh, bx, bw, groundY, t);

    // 选中的那一层
    const sel = this.floorRects.find((f) => f.n === this.selected);
    if (sel) {
      g.strokeStyle = colors.steel;
      g.lineWidth = 2;
      g.strokeRect(sel.r.x - 3, sel.r.y, sel.r.w + 6, sel.r.h);
      g.lineWidth = 1;
    }
    this.finish(W, H, t, sky.night);
    this.drawNotes(W, fh);
  }

  /** 每层右边一条强度条(按做法是图纸的几成,满格是一点二倍),层高够的再写一行字;坏的暖红、差一点的安全黄、够的混凝土灰。 */
  private drawNotes(W: number, fh: number) {
    if (!this.notes) return;
    const g = this.g;
    const barW = Math.min(64, W * 0.12), x1 = W - 10, x0 = x1 - barW;
    const tone = { bad: '#c0563f', warn: '#e8b030', ok: colors.concreteDark };
    g.font = `${Math.min(11, Math.max(9, fh * 0.6))}px system-ui, sans-serif`;
    g.textAlign = 'right';
    g.textBaseline = 'middle';
    for (const { n, r } of this.floorRects) {
      const note = this.notes(n);
      if (!note) continue;
      const cy = r.y + r.h / 2, h = Math.max(2, Math.min(6, fh * 0.35));
      // 底下垫一块纸色:傍晚、夜里的天色上也看得清
      const tw = fh >= 10 ? g.measureText(note.text).width + 8 : 0;
      g.fillStyle = 'rgba(246,241,231,0.85)';
      g.fillRect(x0 - tw - 4, r.y + 0.5, tw + barW + 10, r.h - 1);
      g.fillStyle = 'rgba(0,0,0,0.08)';
      g.fillRect(x0, cy - h / 2, barW, h);
      g.fillStyle = tone[note.tone];
      g.fillRect(x0, cy - h / 2, barW * Math.max(0, Math.min(1, note.bar / 1.2)), h);
      // 图纸的那一格(1.0)画一道细线
      g.fillStyle = 'rgba(0,0,0,0.35)';
      g.fillRect(x0 + barW / 1.2, cy - h / 2 - 1, 1, h + 2);
      if (fh >= 10) {
        g.fillStyle = note.tone === 'bad' ? tone.bad : 'rgba(40,36,30,0.85)';
        g.fillText(note.text, x0 - 6, cy);
      }
    }
    g.textAlign = 'left';
    g.textBaseline = 'alphabetic';
  }

  /** 天气、夜色、窗灯和颗粒最后盖上:夜里整体压暗,窗灯不压暗。 */
  private finish(W: number, H: number, t: number, night: number) {
    const g = this.g;
    if (night > 0) {
      g.fillStyle = `rgba(10,16,40,${night * 0.45})`;
      g.fillRect(0, 0, W, H);
      for (const r of this.lit) {
        g.fillStyle = '#ffd88e';
        g.fillRect(r.x, r.y, r.w, r.h);
        g.fillStyle = 'rgba(255,216,142,0.18)';
        g.fillRect(r.x - 2, r.y - 2, r.w + 4, r.h + 4);
      }
    }
    if (this.weather === 'rain' || this.weather === 'storm' || this.weather === 'typhoon') {
      drawRain(g, W, H, t, this.weather !== 'rain');
    }
    drawGrain(g, W, H);
  }

  private piles(p: Project, bx: number, bw: number, groundY: number, H: number) {
    const g = this.g;
    const shown = Math.min(12, Math.max(4, Math.round(p.piles / 6)));
    const done = p.stage === 'prep' ? 0 : p.stage === 'piles' ? Math.floor(shown * p.stageDone) : shown;
    // 桩长按地下的空间画:30 米画满九成,短桩画得浅
    const depth = (H - groundY) * 0.9 * Math.min(1, Math.max(0.2, p.pileLen / 30));
    for (let i = 0; i < shown; i++) {
      const x = bx + 6 + ((bw - 12) * i) / (shown - 1);
      if (i < done) {
        g.fillStyle = p.pileTier === 'black' ? '#7a7164' : p.pileTier === 'small' ? '#8a8378' : colors.concreteDark;
        g.fillRect(x - 3, groundY, 6, depth);
        g.fillStyle = 'rgba(255,255,255,0.15)';
        g.fillRect(x - 3, groundY, 1.5, depth);
      } else {
        g.strokeStyle = 'rgba(255,255,255,0.18)';
        g.setLineDash([2, 3]);
        g.strokeRect(x - 3, groundY, 6, depth);
        g.setLineDash([]);
      }
    }
  }

  /** 混凝土按档次的颜色:正规厂灰白,小厂偏灰黄,黑作坊发黄发暗。 */
  private concreteOf(tier?: string) {
    return tier === 'black' ? '#a89c84' : tier === 'small' ? '#b1aa9c' : colors.concrete;
  }

  /** 混凝土表面的麻点:黑作坊多、小厂少(按层号固定,每次画都一样)。 */
  private pits(tier: string | undefined, x: number, y: number, w: number, h: number, seed: number) {
    const n = tier === 'black' ? Math.round(w / 3) : tier === 'small' ? Math.round(w / 12) : 0;
    if (!n) return;
    const g = this.g;
    g.fillStyle = 'rgba(60,52,40,0.45)';
    for (let i = 0; i < n; i++) g.fillRect(x + hash(seed * 31 + i) * w, y + hash(seed * 17 + i * 3) * h, 1, 1);
  }

  private floor(p: Project, f: FloorView, r: Rect, t: number, delivered: boolean, night: number) {
    const g = this.g;
    const slab = Math.max(2, r.h * 0.16);
    const col = Math.max(3, r.w * 0.035);
    const cols = [r.x, r.x + r.w * 0.33 - col / 2, r.x + r.w * 0.66 - col / 2, r.x + r.w - col];
    const wood = p.finish.formwork === 'aluminum' ? colors.aluminum : colors.formwork;
    const k = f.phaseDone;

    switch (f.phase) {
      case 'wait':
        return;
      case 'layout':
        g.strokeStyle = 'rgba(255,255,255,0.8)';
        g.setLineDash([4, 3]);
        g.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
        g.setLineDash([]);
        return;
      case 'formwork':
        g.fillStyle = wood;
        g.fillRect(r.x - 3, r.y, (r.w + 6) * k, slab + 1);
        for (const cx of cols) if (cx - r.x <= r.w * k) this.woodColumn(cx, r, col, wood);
        return;
      case 'rebar':
      case 'inspect':
        g.fillStyle = wood;
        g.fillRect(r.x - 3, r.y, r.w + 6, slab + 1);
        for (const cx of cols) this.woodColumn(cx, r, col, wood);
        this.rebar(f, r, f.phase === 'rebar' ? k : 1);
        if (f.phase === 'inspect') this.inspector(r.x + r.w * 0.5, r.y + r.h, t, p.supervisor);
        return;
      case 'pour':
        g.fillStyle = wood;
        g.fillRect(r.x - 3, r.y, r.w + 6, slab + 1);
        for (const cx of cols) this.woodColumn(cx, r, col, wood);
        this.rebar(f, r, 1);
        g.fillStyle = colors.wet;
        g.fillRect(r.x - 2, r.y + 1, (r.w + 4) * k, slab - 1);
        for (const cx of cols) g.fillRect(cx, r.y + r.h - (r.h - slab) * k, col, (r.h - slab) * k);
        // 浆面的一道湿光
        g.fillStyle = 'rgba(255,255,255,0.35)';
        g.fillRect(r.x - 2, r.y + 1, (r.w + 4) * k, 1);
        return;
    }

    // done:结构完成,往后是砌墙、外立面
    const base = facade[p.type] ?? facade.housing;
    const fa = p.facade ? { ...base, wall: p.facade } : base;
    g.fillStyle = this.concreteOf(f.concreteTier);
    g.fillRect(r.x - 3, r.y, r.w + 6, slab);
    for (const cx of cols) g.fillRect(cx, r.y + slab, col, r.h - slab);
    this.pits(f.concreteTier, r.x - 3, r.y, r.w + 6, slab, f.n);
    for (const [i, cx] of cols.entries()) this.pits(f.concreteTier, cx, r.y + slab, col, r.h - slab, f.n * 7 + i);
    g.fillStyle = 'rgba(0,0,0,0.12)';
    g.fillRect(r.x + r.w - col, r.y + slab, col, r.h - slab);
    g.fillRect(r.x - 3, r.y + slab - 1, r.w + 6, 1);
    g.fillStyle = 'rgba(255,255,255,0.18)';
    g.fillRect(r.x - 3, r.y, 1, slab);

    const inner = { x: r.x, y: r.y + slab, w: r.w, h: r.h - slab };
    if (delivered || f.facade >= 1) {
      this.facadeOf(p.type, inner, f.n, p.id, delivered ? night : 0, fa); // 停工关闭的楼没人住,不亮灯
    } else {
      if (f.masonry > 0) this.masonry(inner, f.masonry, cols, col, f.blockTier);
      if (f.facade > 0) {
        const fw = inner.w * f.facade;
        g.save();
        g.beginPath();
        g.rect(inner.x, inner.y, fw, inner.h);
        g.clip();
        this.facadeOf(p.type, inner, f.n, p.id, 0, fa);
        g.restore();
      }
    }
    if (f.reinforced > 0) {
      g.fillStyle = '#4a4f57';
      for (const cx of cols) {
        g.fillRect(cx - 1.5, r.y + slab, col + 3, 2);
        g.fillRect(cx - 1.5, r.y + r.h - 3, col + 3, 2);
      }
    }
    if (f.cracked) this.cracks(r, f.n, t);
  }

  /** 一层楼身上看得见的毛病(见服务端 sim.Marks):按现实留下的痕迹画,细、淡,凑近了才看得清。 */
  private marksOf(kinds: string[], r: Rect, n: number) {
    if (!kinds.length) return;
    const g = this.g;
    const slab = Math.max(2, r.h * 0.16);
    for (const k of kinds) {
      switch (k) {
        case 'honeycomb': {
          // 蜂窝麻面:板边一片小坑
          g.fillStyle = 'rgba(40,36,30,0.55)';
          const x0 = r.x + r.w * (0.15 + hash(n * 7) * 0.5);
          for (let i = 0; i < 14; i++) g.fillRect(x0 + hash(n * 31 + i) * r.w * 0.25, r.y + hash(n * 17 + i * 3) * slab, 1, 1);
          break;
        }
        case 'coldjoint':
          // 冷缝:板边一道细黑线
          g.fillStyle = 'rgba(30,28,24,0.6)';
          g.fillRect(r.x - 3, r.y + slab * 0.5, r.w + 6, 0.8);
          break;
        case 'shrink': {
          // 表面龟裂:几道细细的折线
          g.strokeStyle = 'rgba(50,44,36,0.45)';
          g.lineWidth = 0.6;
          g.beginPath();
          for (let i = 0; i < 4; i++) {
            const x = r.x + hash(n * 13 + i) * r.w;
            g.moveTo(x, r.y + 0.5);
            g.lineTo(x + 2, r.y + slab * 0.5);
            g.lineTo(x + 1, r.y + slab);
          }
          g.stroke();
          g.lineWidth = 1;
          break;
        }
        case 'sag':
          // 板下挠:板底中间往下弯一点
          g.strokeStyle = 'rgba(60,56,50,0.7)';
          g.beginPath();
          g.moveTo(r.x, r.y + slab);
          g.quadraticCurveTo(r.x + r.w / 2, r.y + slab + 3, r.x + r.w, r.y + slab);
          g.stroke();
          break;
        case 'sanding':
          // 雨天浇的:表面起砂发白
          g.fillStyle = 'rgba(255,255,255,0.18)';
          g.fillRect(r.x - 3, r.y, r.w + 6, slab * 0.6);
          break;
        case 'rust':
          // 锈斑:板边挂下几道锈水印
          g.fillStyle = 'rgba(140,72,34,0.5)';
          for (let i = 0; i < 3; i++) g.fillRect(r.x + (0.2 + 0.3 * i + hash(n + i) * 0.1) * r.w, r.y + slab, 1.2, r.h * (0.3 + hash(n * 5 + i) * 0.4));
          break;
        case 'wallcrack':
          // 隔墙斜裂
          g.strokeStyle = 'rgba(45,40,34,0.55)';
          g.lineWidth = 0.7;
          g.beginPath();
          g.moveTo(r.x + r.w * 0.55, r.y + slab + 1);
          g.lineTo(r.x + r.w * 0.6, r.y + r.h * 0.55);
          g.lineTo(r.x + r.w * 0.67, r.y + r.h - 1);
          g.stroke();
          g.lineWidth = 1;
          break;
        case 'boardgap':
          // 外墙掉了一块保温板,露出后面的墙
          g.fillStyle = 'rgba(120,104,86,0.9)';
          g.fillRect(r.x + r.w * 0.2, r.y + slab + 1, Math.max(4, r.w * 0.09), Math.max(2, r.h * 0.35));
          break;
      }
    }
  }

  private woodColumn(cx: number, r: Rect, col: number, wood: string) {
    const g = this.g;
    g.strokeStyle = wood;
    g.lineWidth = 1.5;
    g.strokeRect(cx - 1, r.y + 2, col + 2, r.h - 2);
    g.lineWidth = 1;
  }

  /** 钢筋网:间距按真实参数画,间距放大,网格肉眼可见地变稀。 */
  private rebar(f: FloorView, r: Rect, k: number) {
    const g = this.g;
    const step = Math.max(3, (f.spec.spacing / 150) * 5);
    // 钢筋按档次:正规厂青黑、小厂红褐、黑作坊发黄锈
    g.strokeStyle = f.rebarTier === 'black' ? '#c8743a' : f.rebarTier === 'small' ? '#a8563a' : '#6e5a50';
    g.lineWidth = Math.max(0.6, f.spec.dia / 16);
    g.beginPath();
    const w = r.w * k;
    for (let x = r.x + 2; x < r.x + w; x += step) {
      g.moveTo(x, r.y + 1);
      g.lineTo(x, r.y + r.h * 0.2);
    }
    g.moveTo(r.x, r.y + 2);
    g.lineTo(r.x + w, r.y + 2);
    g.moveTo(r.x, r.y + r.h * 0.16);
    g.lineTo(r.x + w, r.y + r.h * 0.16);
    // 柱筋
    for (const t of [0.02, 0.33, 0.66, 0.98]) {
      if (t > k) continue;
      const x = r.x + r.w * t;
      g.moveTo(x - 1, r.y);
      g.lineTo(x - 1, r.y + r.h);
      g.moveTo(x + 1.5, r.y);
      g.lineTo(x + 1.5, r.y + r.h);
    }
    g.stroke();
    g.lineWidth = 1;
  }

  private masonry(r: Rect, k: number, cols: number[], col: number, tier?: string) {
    const g = this.g;
    const bh = Math.max(2.5, r.h / 6);
    const rows = Math.ceil(r.h / bh);
    const done = Math.floor(rows * k);
    for (let row = 0; row < done; row++) {
      const y = r.y + r.h - (row + 1) * bh;
      for (let x = r.x, n = 0; x < r.x + r.w; x += 9, n++) {
        const bx = x + (row % 2 ? 4.5 : 0);
        // 砌块:黑作坊颜色杂、大小不一
        const jit = tier === 'black' ? (hash(row * 13 + n) - 0.5) * 2 : 0;
        g.fillStyle = tier === 'black' ? (hash(row * 97 + n) < 0.5 ? '#a8987f' : '#9c8a72') : hash(row * 97 + n) < 0.5 ? '#b9a792' : '#ae9b85';
        g.fillRect(bx + jit, y, 8 - Math.abs(jit), bh - 0.8);
      }
    }
    // 窗洞
    g.fillStyle = 'rgba(30,26,22,0.55)';
    for (let i = 0; i < 3; i++) {
      const x0 = cols[i] + col + 4;
      const x1 = cols[i + 1] - 4;
      const top = r.y + r.h * 0.25;
      const h = r.h * 0.5;
      const filled = done * bh;
      if (filled > r.h * 0.3) g.fillRect(x0 + (x1 - x0) * 0.2, Math.max(top, r.y + r.h - filled), (x1 - x0) * 0.6, Math.min(h, filled - r.h * 0.25));
    }
  }

  /** 按楼型画外立面。窗灯亮不亮由楼号和层号决定,同一层每次画都一样。 */
  private facadeOf(type: string, r: Rect, n: number, id: number, night: number, fa: (typeof facade)[string]) {
    const g = this.g;
    g.fillStyle = fa.wall;
    g.fillRect(r.x, r.y, r.w, r.h);
    g.fillStyle = 'rgba(0,0,0,0.1)';
    g.fillRect(r.x + r.w * 0.85, r.y, r.w * 0.15, r.h);
    g.fillStyle = 'rgba(255,255,255,0.25)';
    g.fillRect(r.x, r.y, 1, r.h);
    const on = (i: number) => night > 0 && hash(id * 1009 + n * 31 + i) < 0.25 + night * 0.4;
    if (type === 'office' || type === 'landmark') {
      g.fillStyle = fa.glass;
      g.fillRect(r.x + 2, r.y + 1, r.w - 4, r.h - 3);
      g.fillStyle = 'rgba(0,0,0,0.25)';
      g.fillRect(r.x, r.y + r.h - 2, r.w, 2);
      g.strokeStyle = type === 'landmark' ? 'rgba(201,162,74,0.7)' : 'rgba(255,255,255,0.2)';
      g.beginPath();
      for (let x = r.x + 2; x < r.x + r.w; x += 10) {
        g.moveTo(x, r.y + 1);
        g.lineTo(x, r.y + r.h - 2);
      }
      g.stroke();
      // 一道天光反射
      g.fillStyle = 'rgba(255,255,255,0.12)';
      g.beginPath();
      g.moveTo(r.x + r.w * 0.15, r.y + r.h);
      g.lineTo(r.x + r.w * 0.25, r.y);
      g.lineTo(r.x + r.w * 0.32, r.y);
      g.lineTo(r.x + r.w * 0.22, r.y + r.h);
      g.fill();
      for (let i = 0, x = r.x + 2; x < r.x + r.w - 8; x += 10, i++) if (on(i)) this.lit.push({ x: x + 1, y: r.y + 2, w: 8, h: r.h - 5 });
      return;
    }
    if (type === 'shophouse' && n === 1) {
      // 店屋的底层是铺面:整面玻璃门脸,上面一条帆布雨篷,晚上铺子亮灯
      g.fillStyle = fa.glass;
      g.fillRect(r.x + 3, r.y + r.h * 0.3, r.w - 6, r.h * 0.7);
      g.fillStyle = 'rgba(0,0,0,0.3)';
      for (let x = r.x + 3; x < r.x + r.w - 3; x += Math.max(8, (r.w - 6) / 4)) g.fillRect(x, r.y + r.h * 0.3, 1, r.h * 0.7);
      g.fillStyle = fa.roof;
      g.fillRect(r.x - 3, r.y + r.h * 0.18, r.w + 6, r.h * 0.14);
      g.fillStyle = 'rgba(255,255,255,0.3)';
      for (let x = r.x - 3; x < r.x + r.w + 3; x += 8) g.fillRect(x, r.y + r.h * 0.18, 4, r.h * 0.14);
      if (on(0)) this.lit.push({ x: r.x + 4, y: r.y + r.h * 0.32, w: r.w - 8, h: r.h * 0.66 });
      return;
    }
    const count = type === 'villa' || type === 'shophouse' ? 4 : 6;
    const ww = (r.w / count) * (type === 'villa' || type === 'shophouse' ? 0.55 : 0.42);
    const wh = r.h * (type === 'highrise' ? 0.62 : 0.5);
    const big = r.h >= 12; // 楼层画得够大时才画窗框、阳台、空调外机这些细节
    for (let i = 0; i < count; i++) {
      const x = r.x + (r.w / count) * (i + 0.5) - ww / 2;
      const y = r.y + (r.h - wh) / 2;
      g.fillStyle = fa.glass;
      g.fillRect(x, y, ww, wh);
      // 玻璃上一道天光,窗台一条浅色
      g.fillStyle = 'rgba(255,255,255,0.2)';
      g.fillRect(x, y, ww, 1);
      g.fillStyle = 'rgba(255,255,255,0.35)';
      g.fillRect(x - 1, y + wh, ww + 2, 1);
      if (big) {
        // 窗框:中间一根竖梃
        g.fillStyle = 'rgba(40,40,40,0.35)';
        g.fillRect(x + ww / 2 - 0.4, y, 0.8, wh);
      }
      if (type === 'housing' && i % 2 === 1) {
        // 阳台:挑出的板加栏杆
        g.fillStyle = 'rgba(0,0,0,0.2)';
        g.fillRect(x - 3, y + wh, ww + 6, 2);
        g.strokeStyle = 'rgba(70,70,70,0.55)';
        g.lineWidth = 0.8;
        g.strokeRect(x - 3, y + wh * 0.55, ww + 6, wh * 0.45);
        if (big) {
          g.beginPath();
          for (let k = 1; k < 5; k++) {
            g.moveTo(x - 3 + ((ww + 6) * k) / 5, y + wh * 0.55);
            g.lineTo(x - 3 + ((ww + 6) * k) / 5, y + wh);
          }
          g.stroke();
        }
        g.lineWidth = 1;
      }
      if (big && (type === 'housing' || type === 'highrise') && i % 3 === 0 && hash(id * 7 + n * 13 + i) < 0.7) {
        // 空调外机
        g.fillStyle = '#e9e6df';
        g.fillRect(x + ww + 1, y + wh - 5, 5, 4);
        g.fillStyle = 'rgba(0,0,0,0.25)';
        g.beginPath();
        g.arc(x + ww + 3.5, y + wh - 3, 1.2, 0, Math.PI * 2);
        g.fill();
      }
      if (on(i)) this.lit.push({ x: x + 1, y: y + 1, w: ww - 2, h: wh - 2 });
    }
    // 层间一条细分隔线
    g.fillStyle = 'rgba(0,0,0,0.08)';
    g.fillRect(r.x, r.y + r.h - 1, r.w, 1);
  }

  /** 工人小人:哪个工种在哪一层干活,就画在哪一层。 */
  private workers(p: Project, baseY: number, fh: number, bx: number, bw: number, groundY: number, t: number) {
    const g = this.g;
    const vest: Record<string, string> = {
      formwork: '#c8763a', rebar: '#b0452f', concrete: '#6a6f78', mason: '#8a6d3b', mep: '#2f6db0', finish: '#3d8757', waterproof: '#555',
    };
    Object.entries(p.doing ?? {}).forEach(([trade, a], ti) => {
      const n = trade === 'mason' || trade === 'mep' ? 2 : 3;
      for (let k = 0; k < n; k++) {
        let foot: number;
        if (a.floor > 0) foot = baseY - (a.floor - 1) * fh - 1;
        else if (trade === 'waterproof') foot = baseY - p.floors.length * fh;
        else foot = groundY - 8;
        const lane = (ti * 37 + k * 23) % 100;
        const walk = Math.sin(t / (700 + k * 130) + ti + k) * 6;
        const x = bx + bw * (0.12 + (lane / 100) * 0.76) + walk;
        const h = Math.max(6, Math.min(11, fh * 0.55));
        // 身子(反光背心)、头、安全帽
        g.fillStyle = vest[trade] ?? '#777';
        g.fillRect(x - h * 0.18, foot - h * 0.75, h * 0.36, h * 0.45);
        g.fillStyle = '#3a3a3a';
        g.fillRect(x - h * 0.15, foot - h * 0.3, h * 0.12, h * 0.3);
        g.fillRect(x + h * 0.03, foot - h * 0.3, h * 0.12, h * 0.3);
        g.fillStyle = '#e8c9a3';
        g.beginPath();
        g.arc(x, foot - h * 0.85, h * 0.13, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = colors.steel;
        g.beginPath();
        g.arc(x, foot - h * 0.9, h * 0.16, Math.PI, 0);
        g.fill();
      }
    });
  }

  private cracks(r: Rect, n: number, t: number) {
    const g = this.g;
    const a = 0.65 + 0.35 * Math.sin(t / 500);
    g.strokeStyle = `rgba(160,50,35,${a})`;
    g.lineWidth = 1.2;
    g.beginPath();
    for (let i = 0; i < 3; i++) {
      let x = r.x + r.w * (0.15 + hash(n * 7 + i) * 0.7);
      let y = r.y + 2;
      g.moveTo(x, y);
      for (let s = 0; s < 4; s++) {
        x += (hash(n * 13 + i * 5 + s) - 0.3) * 8;
        y += r.h / 4;
        g.lineTo(x, y);
      }
    }
    g.stroke();
    g.lineWidth = 1;
  }

  private roof(p: Project, bx: number, bw: number, y: number, t: number) {
    const g = this.g;
    const fa = facade[p.type] ?? facade.housing;
    if (p.type === 'villa') {
      g.fillStyle = fa.roof;
      g.beginPath();
      g.moveTo(bx - 10, y);
      g.lineTo(bx + bw / 2, y - Math.min(40, bw * 0.22));
      g.lineTo(bx + bw + 10, y);
      g.closePath();
      g.fill();
      g.fillStyle = 'rgba(0,0,0,0.15)';
      g.beginPath();
      g.moveTo(bx + bw / 2, y - Math.min(40, bw * 0.22));
      g.lineTo(bx + bw + 10, y);
      g.lineTo(bx + bw / 2, y);
      g.fill();
    } else {
      // 女儿墙、楼梯间和电梯机房、屋顶水箱
      g.fillStyle = fa.roof;
      g.fillRect(bx - 2, y - 4, bw + 4, 4);
      g.fillRect(bx + bw * 0.58, y - 14, bw * 0.18, 10);
      g.fillStyle = 'rgba(0,0,0,0.15)';
      g.fillRect(bx + bw * 0.58 + bw * 0.18 - 3, y - 14, 3, 10);
      g.fillStyle = '#b9b4ab';
      g.fillRect(bx + bw * 0.2, y - 12, bw * 0.12, 8);
      g.fillStyle = '#6d6a64';
      g.fillRect(bx + bw * 0.2 + 2, y - 4, 2, 0);
      g.strokeStyle = '#6d6a64';
      g.beginPath();
      g.moveTo(bx + bw * 0.22, y - 4);
      g.lineTo(bx + bw * 0.22, y);
      g.moveTo(bx + bw * 0.3, y - 4);
      g.lineTo(bx + bw * 0.3, y);
      g.stroke();
      if (p.type === 'landmark') {
        g.fillStyle = '#333';
        g.fillRect(bx + bw / 2 - 1, y - 46, 2, 42);
        g.fillStyle = Math.sin(t / 400) > 0 ? '#e0473a' : '#7a2a22';
        g.beginPath();
        g.arc(bx + bw / 2, y - 47, 2.5, 0, Math.PI * 2);
        g.fill();
      }
    }
    if (p.grade === 'S') {
      // S 级:楼顶一道金光
      const grad = g.createRadialGradient(bx + bw / 2, y - 10, 2, bx + bw / 2, y - 10, bw * 0.6);
      grad.addColorStop(0, 'rgba(224,178,76,0.55)');
      grad.addColorStop(1, 'rgba(224,178,76,0)');
      g.fillStyle = grad;
      g.fillRect(bx - bw * 0.2, y - bw * 0.6, bw * 1.4, bw * 0.8);
    }
  }

  private fence(bx: number, bw: number, groundY: number, W: number) {
    const g = this.g;
    const x0 = Math.max(4, bx - 70);
    const x1 = Math.min(W - 4, bx + bw + 90);
    g.fillStyle = '#e9e3d6';
    g.fillRect(x0, groundY - 12, x1 - x0, 12);
    g.fillStyle = colors.steel;
    for (let x = x0; x < x1; x += 24) g.fillRect(x, groundY - 12, 12, 2);
    g.fillStyle = 'rgba(0,0,0,0.1)';
    g.fillRect(x0, groundY - 1, x1 - x0, 1);
  }

  private scaffold(x: number, baseY: number, top: number, fh: number) {
    const g = this.g;
    const h = (top + 0.6) * fh;
    g.strokeStyle = 'rgba(80,80,80,0.6)';
    g.beginPath();
    g.moveTo(x + 4, baseY + 8);
    g.lineTo(x + 4, baseY - h);
    g.moveTo(x + 12, baseY + 8);
    g.lineTo(x + 12, baseY - h);
    for (let y = baseY; y > baseY - h; y -= fh) {
      g.moveTo(x + 4, y);
      g.lineTo(x + 12, y);
    }
    g.stroke();
    g.fillStyle = colors.net;
    g.fillRect(x + 12, baseY - h, 3, h + 8);
  }

  private crane(x: number, groundY: number, topY: number, bw: number, t: number, target: { x: number; y: number; load: string }) {
    const g = this.g;
    // 塔身:黄色桁架
    g.strokeStyle = colors.steel;
    g.lineWidth = 1.2;
    g.beginPath();
    g.moveTo(x - 4, groundY);
    g.lineTo(x - 4, topY);
    g.moveTo(x + 4, groundY);
    g.lineTo(x + 4, topY);
    for (let y = groundY; y > topY; y -= 10) {
      g.moveTo(x - 4, y);
      g.lineTo(x + 4, y - 10);
    }
    g.stroke();
    // 吊臂、配重
    const jib = bw + 60;
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(x - 36, topY);
    g.lineTo(x + jib, topY);
    g.stroke();
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(x, topY - 14);
    g.lineTo(x + jib * 0.8, topY);
    g.moveTo(x, topY - 14);
    g.lineTo(x - 30, topY);
    g.moveTo(x, topY);
    g.lineTo(x, topY - 14);
    g.stroke();
    g.fillStyle = colors.steelDark;
    g.fillRect(x - 36, topY, 12, 7);
    g.fillRect(x + 5, topY + 1, 7, 6);
    // 一趟:在楼外地面挂料 → 起吊 → 小车开到楼上方 → 放到那一层 → 空钩回去。一趟 14 秒。
    const pickX = x + jib - 8;
    const k = (t % 14000) / 14000;
    const ease = (v: number) => v * v * (3 - 2 * v);
    let tx: number, hookY: number, loaded: boolean;
    if (k < 0.15) [tx, hookY, loaded] = [pickX, topY + 20 + (groundY - 30 - topY - 20) * ease(k / 0.15), k > 0.1];
    else if (k < 0.35) [tx, hookY, loaded] = [pickX, groundY - 30 - (groundY - 30 - topY - 20) * ease((k - 0.15) / 0.2), true];
    else if (k < 0.55) [tx, hookY, loaded] = [pickX + (target.x - pickX) * ease((k - 0.35) / 0.2), topY + 20, true];
    else if (k < 0.7) [tx, hookY, loaded] = [target.x, topY + 20 + (target.y - 14 - topY - 20) * ease((k - 0.55) / 0.15), true];
    else if (k < 0.78) [tx, hookY, loaded] = [target.x, target.y - 14, false];
    else [tx, hookY, loaded] = [target.x + (pickX - target.x) * ease((k - 0.78) / 0.22), target.y - 14 - (target.y - 14 - topY - 20) * ease((k - 0.78) / 0.22), false];
    g.strokeStyle = 'rgba(40,35,25,0.7)';
    g.beginPath();
    g.moveTo(tx, topY);
    g.lineTo(tx, hookY);
    g.stroke();
    g.fillStyle = colors.steelDark;
    g.fillRect(tx - 3, topY, 6, 3);
    g.fillRect(tx - 1.5, hookY, 3, 4);
    if (loaded) {
      // 吊索和料
      g.strokeStyle = 'rgba(40,35,25,0.6)';
      g.beginPath();
      g.moveTo(tx, hookY + 4);
      g.lineTo(tx - 8, hookY + 9);
      g.moveTo(tx, hookY + 4);
      g.lineTo(tx + 8, hookY + 9);
      g.stroke();
      if (target.load === 'rebar') {
        g.fillStyle = colors.rebar;
        for (let i = 0; i < 3; i++) g.fillRect(tx - 11, hookY + 9 + i * 1.6, 22, 1);
      } else if (target.load === 'wood') {
        g.fillStyle = colors.formwork;
        g.fillRect(tx - 10, hookY + 9, 20, 4);
        g.fillStyle = 'rgba(0,0,0,0.2)';
        g.fillRect(tx - 10, hookY + 11, 20, 0.6);
      } else {
        g.fillStyle = '#b9a792';
        g.fillRect(tx - 8, hookY + 9, 16, 6);
        g.fillStyle = '#8a6d3b';
        g.fillRect(tx - 9, hookY + 15, 18, 1.5);
      }
    }
  }

  private pump(bx: number, groundY: number, targetY: number, W: number) {
    const g = this.g;
    const tx = Math.min(W - 70, bx + 50);
    g.fillStyle = '#d9d4ca';
    g.fillRect(tx, groundY - 14, 54, 11);
    g.fillStyle = colors.steel;
    g.fillRect(tx + 38, groundY - 20, 16, 17);
    g.fillStyle = '#333';
    for (const wx of [tx + 8, tx + 22, tx + 46]) {
      g.beginPath();
      g.arc(wx, groundY - 3, 3.5, 0, Math.PI * 2);
      g.fill();
    }
    // 臂架伸到正在浇的这一层
    g.strokeStyle = '#7d7a73';
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(tx + 12, groundY - 14);
    const midX = tx + 20;
    const midY = Math.min(groundY - 40, targetY - 20);
    g.lineTo(midX, midY);
    g.lineTo(bx - 20, targetY - 6);
    g.lineTo(bx - 20, targetY);
    g.stroke();
    g.lineWidth = 1;
  }

  private pileDriver(x: number, groundY: number, t: number) {
    const g = this.g;
    g.fillStyle = colors.steel;
    g.fillRect(x - 12, groundY - 12, 24, 10);
    g.fillStyle = colors.steelDark;
    g.fillRect(x - 2, groundY - 72, 4, 62);
    const up = (Math.sin(t / 260) * 0.5 + 0.5) * 18;
    g.fillStyle = '#555';
    g.fillRect(x - 5, groundY - 70 + up, 10, 12);
  }

  private hut(x: number, groundY: number) {
    const g = this.g;
    g.fillStyle = '#9fb6c8';
    g.fillRect(x, groundY - 22, 44, 22);
    g.fillStyle = '#d0dde6';
    g.fillRect(x - 2, groundY - 25, 48, 3);
    g.fillStyle = '#4d6778';
    g.fillRect(x + 6, groundY - 16, 10, 7);
    g.fillRect(x + 26, groundY - 16, 10, 7);
  }

  private inspector(x: number, footY: number, t: number, supervisor: string) {
    const g = this.g;
    if (supervisor === 'none') return;
    const dx = Math.sin(t / 900) * 18;
    g.fillStyle = '#f3f0e8';
    g.beginPath();
    g.arc(x + dx, footY - 11, 3, Math.PI, 0);
    g.fill();
    g.fillStyle = '#2d4f7c';
    g.fillRect(x + dx - 2.5, footY - 10, 5, 7);
    g.fillStyle = '#333';
    g.fillRect(x + dx - 2, footY - 3, 1.5, 3);
    g.fillRect(x + dx + 0.5, footY - 3, 1.5, 3);
    g.fillStyle = '#f7f2e4';
    g.fillRect(x + dx + 3, footY - 9, 3, 4);
  }

  private rubble(bx: number, bw: number, groundY: number, floors: number, fh: number) {
    const g = this.g;
    const h = Math.min(60, 12 + floors * fh * 0.15);
    for (let i = 0; i < 60; i++) {
      const x = bx - 30 + hash(i * 7) * (bw + 90);
      const d = Math.abs(x - (bx + bw / 2)) / (bw / 2 + 45);
      const y = groundY - (1 - d) * h * hash(i * 11);
      g.fillStyle = i % 4 === 0 ? colors.rebar : i % 3 ? colors.concreteDark : colors.concrete;
      g.save();
      g.translate(x, y);
      g.rotate(hash(i * 13) * 3);
      g.fillRect(-6, -3, i % 4 === 0 ? 16 : 12, i % 4 === 0 ? 1.5 : 6);
      g.restore();
    }
  }
}
